import { MVTLayer } from '@deck.gl/geo-layers';
import { PathStyleExtension } from '@deck.gl/extensions';
import type { Color } from '@deck.gl/core';
import { getDefaultConfig } from '.';
import { OVERVIEW_ZOOM_THRESHOLD } from '@/app/constants';
import { getMantineTheme } from '@/assets/styles/themeMantine';
import { hexToRgba } from '@/helpers/colors';

// Not a LayerType member: the outline is never toggled per objective or by the user, so it
// stays out of the layer config machinery. The id doubles as the MVT path segment.
const NETWORK_OUTLINE_ID = 'network_outline';

const { colors } = getMantineTheme();
const outline = hexToRgba(colors.grayBrand[3], 255);
const OUTLINE_COLOR: Color = [outline.r, outline.g, outline.b, outline.a];

// Dash and gap are relative to line width, so this draws a 3px dash and a 6px gap. The
// gap has to run longer than the dash to read as one: rounded caps add half a line width
// of ink at each end, spending 1.5px of the gap and stretching the dash by as much.
const DASH_ARRAY: [number, number] = [2, 4];
const LINE_WIDTH = 1.5;

const dashed = new PathStyleExtension({ dash: true });

/** Whether the greyed-out network belongs on the map for this zoom and filter state. */
export const shouldShowNetworkOutline = ({
  flagEnabled,
  zoom,
  isFilterActive,
}: {
  flagEnabled: boolean;
  zoom: number | undefined;
  isFilterActive: boolean;
}): boolean =>
  flagEnabled &&
  isFilterActive &&
  zoom !== undefined &&
  zoom <= OVERVIEW_ZOOM_THRESHOLD;

/**
 * The whole network as a grey dashed line, so spans a filter hides keep their context.
 *
 * Drawn under the coloured network, which covers the spans the filter keeps. Above
 * OVERVIEW_ZOOM_THRESHOLD the lines layer already outlines the network, so this stops
 * where that one starts.
 */
export const getNetworkOutlineLayer = (
  user: string = '',
  minZoom = 5,
  maxZoom = 14
) =>
  new MVTLayer({
    ...getDefaultConfig(NETWORK_OUTLINE_ID, user),
    id: NETWORK_OUTLINE_ID,
    minZoom,
    maxZoom,
    pickable: false,
    autoHighlight: false,
    filled: false,
    stroked: true,
    getLineColor: OUTLINE_COLOR,
    getLineWidth: LINE_WIDTH,
    lineWidthUnits: 'pixels',
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 2,
    lineCapRounded: true,
    lineJointRounded: true,
    getDashArray: DASH_ARRAY,
    dashJustified: true,
    extensions: [dashed],
  });
